import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { DEFAULT_LOCALE, I18N, Locale, LOCALE_STORAGE_KEY, SEO_COPY } from './landing';

type LocaleContextValue = {
  locale: Locale;
  setLocale: (loc: Locale) => void;
  t: (key: string) => string;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

const isLocale = (v: string | null): v is Locale => v === 'en' || v === 'sk' || v === 'ua';

export const LocaleProvider = ({ children }: { children: ReactNode }): React.JSX.Element => {
  const [locale, setLocaleState] = useState<Locale>(() => {
    if (typeof window === 'undefined') return DEFAULT_LOCALE;
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get('lang');
    if (isLocale(fromQuery)) return fromQuery;
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (isLocale(stored)) return stored;
    const browser = (window.navigator.language || '').slice(0, 2).toLowerCase();
    if (browser === 'sk') return 'sk';
    if (browser === 'uk' || browser === 'ru') return 'ua';
    return DEFAULT_LOCALE;
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-locale', locale);
    document.documentElement.setAttribute('lang', SEO_COPY[locale].htmlLang);
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  }, [locale]);

  const setLocale = useCallback((loc: Locale) => setLocaleState(loc), []);

  const t = useCallback(
    (key: string): string => {
      const dict = I18N[locale];
      return dict[key] ?? I18N[DEFAULT_LOCALE][key] ?? key;
    },
    [locale],
  );

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

export const useLocale = (): LocaleContextValue => {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within a LocaleProvider');
  return ctx;
};

type HtmlTag = keyof React.JSX.IntrinsicElements;

type HtmlProps = Omit<React.HTMLAttributes<HTMLElement>, 'children'> & {
  tag?: HtmlTag;
  tKey: string;
};

export const TransHtml = ({ tKey, tag = 'span', ...rest }: HtmlProps): React.JSX.Element => {
  const { t } = useLocale();
  return React.createElement(tag as string, {
    ...rest,
    dangerouslySetInnerHTML: { __html: t(tKey) },
  });
};
